/**
 * React hooks for compliance verification workflows
 */

import { useState, useCallback, useRef } from 'react';
import type { ComplianceClient } from './client';
import type {
  RegistrationVerificationRequest,
  RegistrationVerificationResponse,
  LoginVerificationRequest,
  LoginVerificationResponse,
  TransactionVerificationRequest,
  TransactionVerificationResponse,
} from './types';

// ============================================================================
// useRegistration Hook
// ============================================================================

export interface UseRegistrationOptions {
  onSuccess?: (result: RegistrationVerificationResponse) => void;
  onBlocked?: (result: RegistrationVerificationResponse) => void;
  onError?: (error: Error) => void;
}

export interface UseRegistrationResult {
  verification: RegistrationVerificationResponse | null;
  loading: boolean;
  error: Error | null;
  verifyRegistration: (request: RegistrationVerificationRequest) => Promise<RegistrationVerificationResponse>;
}

/**
 * Hook for registration verification workflow
 *
 * @param client - ComplianceClient instance
 * @param options - Hook options with callbacks
 *
 * @example
 * ```typescript
 * const { verifyRegistration, loading, error } = useRegistration(sdk, {
 *   onSuccess: (result) => {
 *     console.log('Registration approved!', result.profile);
 *     navigate('/dashboard');
 *   },
 *   onBlocked: (result) => {
 *     alert(`Registration blocked: ${result.blockReasons.join(', ')}`);
 *   }
 * });
 *
 * const handleSubmit = async (formData) => {
 *   await verifyRegistration({
 *     customerId: formData.customerId,
 *     fullName: formData.fullName,
 *     emailAddress: formData.email,
 *     ipAddress: await getClientIP(),
 *     deviceFingerprint: getDeviceFingerprint()
 *   });
 * };
 * ```
 */
export function useRegistration(
  client: ComplianceClient,
  options: UseRegistrationOptions = {}
): UseRegistrationResult {
  const [verification, setVerification] = useState<RegistrationVerificationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const verifyRegistration = useCallback(
    async (request: RegistrationVerificationRequest) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.verifyAtRegistration(request);
        setVerification(result);

        if (result.allowed && optionsRef.current.onSuccess) {
          optionsRef.current.onSuccess(result);
        }

        if (!result.allowed && optionsRef.current.onBlocked) {
          optionsRef.current.onBlocked(result);
        }

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Registration verification failed');
        setError(error);

        if (optionsRef.current.onError) {
          optionsRef.current.onError(error);
        }

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    verification,
    loading,
    error,
    verifyRegistration,
  };
}

// ============================================================================
// useLoginVerification Hook
// ============================================================================

export interface UseLoginVerificationOptions {
  onSuccess?: (result: LoginVerificationResponse) => void;
  onBlocked?: (result: LoginVerificationResponse) => void;
  onStepUpRequired?: (result: LoginVerificationResponse) => void;
  onError?: (error: Error) => void;
}

export interface UseLoginVerificationResult {
  verification: LoginVerificationResponse | null;
  loading: boolean;
  error: Error | null;
  verifyLogin: (request: LoginVerificationRequest) => Promise<LoginVerificationResponse>;
}

/**
 * Hook for login verification workflow
 *
 * @param client - ComplianceClient instance
 * @param options - Hook options with callbacks
 */
export function useLoginVerification(
  client: ComplianceClient,
  options: UseLoginVerificationOptions = {}
): UseLoginVerificationResult {
  const [verification, setVerification] = useState<LoginVerificationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const verifyLogin = useCallback(
    async (request: LoginVerificationRequest) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.verifyAtLogin(request);
        setVerification(result);

        if (result.allowed) {
          if (result.requiresStepUp && optionsRef.current.onStepUpRequired) {
            optionsRef.current.onStepUpRequired(result);
          } else if (optionsRef.current.onSuccess) {
            optionsRef.current.onSuccess(result);
          }
        } else if (optionsRef.current.onBlocked) {
          optionsRef.current.onBlocked(result);
        }

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Login verification failed');
        setError(error);

        if (optionsRef.current.onError) {
          optionsRef.current.onError(error);
        }

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    verification,
    loading,
    error,
    verifyLogin,
  };
}

// ============================================================================
// useTransactionVerification Hook
// ============================================================================

export interface UseTransactionVerificationOptions {
  onSuccess?: (result: TransactionVerificationResponse) => void;
  onBlocked?: (result: TransactionVerificationResponse) => void;
  onApprovalRequired?: (result: TransactionVerificationResponse) => void;
  onError?: (error: Error) => void;
}

export interface UseTransactionVerificationResult {
  verification: TransactionVerificationResponse | null;
  loading: boolean;
  error: Error | null;
  verifyTransaction: (request: TransactionVerificationRequest) => Promise<TransactionVerificationResponse>;
}

/**
 * Hook for transaction verification workflow
 *
 * @param client - ComplianceClient instance
 * @param options - Hook options with callbacks
 *
 * @example
 * ```typescript
 * const { verifyTransaction, loading } = useTransactionVerification(sdk, {
 *   onSuccess: (result) => {
 *     processTransaction(result);
 *   },
 *   onApprovalRequired: (result) => {
 *     queueForReview(result);
 *   },
 *   onBlocked: (result) => {
 *     alert(`Transaction blocked: ${result.blockReasons.join(', ')}`);
 *   }
 * });
 * ```
 */
export function useTransactionVerification(
  client: ComplianceClient,
  options: UseTransactionVerificationOptions = {}
): UseTransactionVerificationResult {
  const [verification, setVerification] = useState<TransactionVerificationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const verifyTransaction = useCallback(
    async (request: TransactionVerificationRequest) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.verifyAtTransaction(request);
        setVerification(result);

        if (result.allowed && optionsRef.current.onSuccess) {
          optionsRef.current.onSuccess(result);
        } else if (result.requiresApproval && optionsRef.current.onApprovalRequired) {
          optionsRef.current.onApprovalRequired(result);
        } else if (!result.allowed && optionsRef.current.onBlocked) {
          optionsRef.current.onBlocked(result);
        }

        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Transaction verification failed');
        setError(error);

        if (optionsRef.current.onError) {
          optionsRef.current.onError(error);
        }

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    verification,
    loading,
    error,
    verifyTransaction,
  };
}
